// HowItLands — the side panel "How it lands in Torus / Canvas" (pen "Tavla ·
// Always writing", scene C; "Panel · I Torus"). Opened from Export in place
// of the action panel, × gives the action panel back. For the format the
// project is made for, each block of the open page with its fate: arrives
// as it is (check), changes shape (arrow-right-left), not sent (eye-off).
// The answer is the server's, derived on every ask, so it cannot go stale.
// Then the assistant's proposals for the odd ones (Show me · Move it; it
// never moves text by itself), and at the foot "Export to <format>".
//
// Cases: Waiting line while the answer is on its way; "Could not work out
// how this lands in <format>. Try again." when it fails (Export still
// works); a block kind the client has no word for is "not sent" under its
// own kind name. Pointing at a row tints its blocks on the page.
//
// Pure: the answer and the open page are projected in; every choice goes
// out as an intent.

export { HowItLands, LandingProposalCard };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type {
    LandingFormat,
    LandingReshape,
    MoveTextProposal,
} from "../../app/ports/landing.js";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import type {
    LandingAnswer,
    LandingRow,
    LandingRowHow,
    LandingRowWhat,
    LandingView,
} from "./landingView.js";
import "./HowItLands.css";

// The format's name; a proper noun, the same in every language.
const formatName = (format: LandingFormat): string => {
    switch (format) {
        case "torus":
            return "Torus";
        case "canvas":
            return "Canvas";
        default:
            return exhaustSwitchCase(format);
    }
};

// What the open page is (for "first in the module").
type PageKind = "course" | "module" | "page";

const useKindName = (): ((kind: string) => string) => {
    const { t } = useLingui();
    return (kind) => {
        switch (kind) {
            case "prose":
                return t`Text`;
            case "heading":
                return t`Heading`;
            case "list":
                return t`List`;
            case "question_single":
                return t`Question`;
            case "media":
                return t`Image`;
            case "section":
                return t`Section`;
            case "include":
                return t`Part`;
            case "note":
                return t`Your note`;
            default:
                return kind;
        }
    };
};

// A kind in "has no place for …"; a kind with no word here is named as it
// is (an unknown kind is never hidden).
const useKindPhrase = (): ((kind: string) => string) => {
    const { t } = useLingui();
    return (kind) => {
        switch (kind) {
            case "prose":
                return t`this text`;
            case "heading":
                return t`a heading`;
            case "list":
                return t`a list`;
            case "question_single":
                return t`a question`;
            case "media":
                return t`an image`;
            case "section":
                return t`a section`;
            case "include":
                return t`a part`;
            default:
                return t`a ${kind}`;
        }
    };
};

const useWhat = (): ((what: LandingRowWhat) => string) => {
    const { t } = useLingui();
    const kindName = useKindName();
    return (what) => {
        switch (what.type) {
            case "text_at_top":
                return t`Text at the top`;
            case "text_between":
                return t`Text between parts`;
            case "text_at_end":
                return t`Text at the end`;
            case "part":
                return what.title;
            case "kind":
                return kindName(what.kind);
            case "the_rest":
                return what.alone ? t`Everything on this page` : t`The rest`;
            default:
                return exhaustSwitchCase(what);
        }
    };
};

const useReshape = (
    format: LandingFormat,
    page: PageKind,
): ((into: LandingReshape) => string) => {
    const { t } = useLingui();
    const name = formatName(format);
    return (into) => {
        switch (into.type) {
            case "page_of_its_own": {
                const title = into.title;
                const place = into.place;
                switch (place.type) {
                    case "first": {
                        const first =
                            page === "course"
                                ? t`Becomes a page of its own, ${title}, first in the course.`
                                : t`Becomes a page of its own, ${title}, first in the module.`;
                        // Torus holds text only on pages; Canvas modules can
                        // hold text headers, so nothing is said of Canvas.
                        return format === "torus"
                            ? `${first} ${t`Torus modules hold pages, not text.`}`
                            : first;
                    }
                    case "before": {
                        const part = place.part;
                        return t`Becomes a short page before ${part}.`;
                    }
                    case "after": {
                        const part = place.part;
                        return t`Becomes a short page after ${part}.`;
                    }
                    default:
                        return exhaustSwitchCase(place);
                }
            }
            case "ordinary_text":
                return t`Arrives as ordinary text.`;
            case "module_of_its_own":
                return t`Gets a module of its own around it, at the top of the course.`;
            case "text_header": {
                const within = into.within;
                return t`Becomes a text header in ${within}, with its parts listed under it. ${name} modules do not hold modules.`;
            }
            case "page_and_quiz":
                return t`Becomes a page and a quiz of its own, the questions after the text.`;
            case "quiz_after_page": {
                const pageTitle = into.page;
                return t`Goes to a quiz after the text of ${pageTitle}.`;
            }
            case "name_only":
                return t`Only its name arrives. The file itself is not attached.`;
            case "unknown":
                return t`Changes shape.`;
            default:
                return exhaustSwitchCase(into);
        }
    };
};

const useHow = (
    format: LandingFormat,
    page: PageKind,
): ((how: LandingRowHow) => string) => {
    const { t } = useLingui();
    const reshape = useReshape(format, page);
    const kindPhrase = useKindPhrase();
    const name = formatName(format);
    return (how) => {
        switch (how.type) {
            case "nothing_of_it":
                return t`Not sent. Nothing in it reaches ${name} yet.`;
            case "fate": {
                const fate = how.fate;
                switch (fate.type) {
                    case "kept":
                        return t`Arrive as they are.`;
                    case "reshaped":
                        return reshape(fate.into);
                    case "not_sent": {
                        if (fate.kind === "note")
                            return t`Not sent. Notes stay with you and the assistant.`;
                        const kind = kindPhrase(fate.kind);
                        return t`Not sent. ${name} has no place for ${kind}.`;
                    }
                    default:
                        return exhaustSwitchCase(fate);
                }
            }
            default:
                return exhaustSwitchCase(how);
        }
    };
};

const rowIcon = (
    how: LandingRowHow,
): "check" | "arrow-right-left" | "eye-off" => {
    switch (how.type) {
        case "nothing_of_it":
            return "eye-off";
        case "fate":
            switch (how.fate.type) {
                case "kept":
                    return "check";
                case "reshaped":
                    return "arrow-right-left";
                case "not_sent":
                    return "eye-off";
                default:
                    return exhaustSwitchCase(how.fate);
            }
        default:
            return exhaustSwitchCase(how);
    }
};

// One proposal (pen "Proposal"): what the assistant proposes, Show me and
// Move it. Shared with Needs you.
const LandingProposalCard = ({
    proposal,
    online,
    moving,
    onShowMe,
    onMoveIt,
}: {
    readonly proposal: MoveTextProposal;
    readonly online: boolean;
    // This one is being moved (Move it is off until it answers).
    readonly moving: boolean;
    readonly onShowMe: () => void;
    readonly onMoveIt: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const title = proposal.partTitle;
    return (
        <section className="bilda-landing-proposal" aria-label={t`Proposal`}>
            <header className="bilda-landing-proposal__head">
                <span className="bilda-landing-proposal__title">{t`Proposal`}</span>
                <AiMarker variant="chip" />
            </header>
            <p className="bilda-landing-proposal__text">
                {t`The text between parts reads like the start of ${title}. Move it there instead of a page of its own?`}
            </p>
            <div className="bilda-landing-proposal__actions">
                <Button variant="secondary" size="sm" onClick={onShowMe}>
                    {t`Show me`}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={!online || moving}
                    onClick={onMoveIt}
                >
                    {t`Move it`}
                </Button>
            </div>
        </section>
    );
};

const HowItLands = ({
    format,
    page,
    answer,
    view,
    proposals,
    online,
    moving,
    onClose,
    onRetry,
    onExport,
    onPoint,
    onShowMe,
    onMoveIt,
}: {
    readonly format: LandingFormat;
    readonly page: PageKind;
    readonly answer: LandingAnswer;
    // The answer for the open page (absent: no answer yet, or it failed).
    readonly view: LandingView | undefined;
    readonly proposals: readonly MoveTextProposal[];
    readonly online: boolean;
    // The proposal being moved, by its part (absent: none).
    readonly moving: string | undefined;
    readonly onClose: () => void;
    readonly onRetry: () => void;
    readonly onExport: () => void;
    // Pointing at a row (none: pointing at nothing).
    readonly onPoint: (ids: readonly string[]) => void;
    readonly onShowMe: (proposal: MoveTextProposal) => void;
    readonly onMoveIt: (proposal: MoveTextProposal) => void;
}): ReactNode => {
    const { t } = useLingui();
    const name = formatName(format);
    const what = useWhat();
    const how = useHow(format, page);
    const body = ((): ReactNode => {
        switch (answer.type) {
            case "waiting":
                return (
                    <p className="bilda-landing__waiting" role="status">
                        <Icon name="loading" size="sm" />
                        {t`Working out how this lands in ${name} …`}
                    </p>
                );
            case "failed":
                return (
                    <div className="bilda-landing__error" role="alert">
                        <p>{t`Could not work out how this lands in ${name}. Try again.`}</p>
                        <Button variant="secondary" size="sm" onClick={onRetry}>
                            {t`Try again`}
                        </Button>
                    </div>
                );
            case "landed":
                return view === undefined || view.rows.length === 0 ? (
                    <p className="bilda-landing__empty">
                        {t`Nothing on this page goes to ${name}.`}
                    </p>
                ) : (
                    <ul className="bilda-landing__rows">
                        {view.rows.map((row: LandingRow) => (
                            <li
                                key={row.key}
                                className="bilda-landing__row"
                                data-icon={rowIcon(row.how)}
                                onPointerEnter={() =>
                                    onPoint([...row.blocks, ...row.parts])
                                }
                                onPointerLeave={() => onPoint([])}
                            >
                                <Icon name={rowIcon(row.how)} size="sm" />
                                <span className="bilda-landing__row-text">
                                    <span className="bilda-landing__what">
                                        {what(row.what)}
                                    </span>
                                    <span className="bilda-landing__how">
                                        {how(row.how)}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                );
            default:
                return exhaustSwitchCase(answer);
        }
    })();
    return (
        <aside
            className="bilda-landing"
            aria-label={t`How it lands in ${name}`}
        >
            <header className="bilda-landing__head">
                <h2 className="bilda-landing__title">{t`How it lands in ${name}`}</h2>
                <IconButton
                    variant="ghost"
                    size="sm"
                    label={t`Close`}
                    onClick={onClose}
                >
                    <Icon name="close" size="sm" />
                </IconButton>
            </header>
            <p className="bilda-landing__subtitle">
                {t`Made for ${name}. Your page stays as you wrote it, this only shows what the course will look like there.`}
            </p>
            {body}
            {proposals.map((p) => (
                <LandingProposalCard
                    key={`${p.container}:${p.part}`}
                    proposal={p}
                    online={online}
                    moving={moving === p.part}
                    onShowMe={() => onShowMe(p)}
                    onMoveIt={() => onMoveIt(p)}
                />
            ))}
            <footer className="bilda-landing__foot">
                <Button variant="primary" disabled={!online} onClick={onExport}>
                    {t`Export to ${name}`}
                </Button>
            </footer>
        </aside>
    );
};
